import { IPacmanGameData } from "design-system/src/Components/WidgetPart/LandingPacman/LandingPacman";
import Modal, { IModalProps } from "design-system/src/Core/Modal/Modal";
import Icon from "design-system/src/Foundation/Icon/Icon";
import React, { useCallback, useState } from "react";
import { _AccountOrderHelpModalBody } from "./GameParticipationModal.styled";
import useBreakpoint from "design-system/src/Components/Grid/hooks/useBreakpoint";
import Typography from "design-system/src/Foundation/Typography/Typography";
import { useTranslation } from "@src/hooks/useTranslation";
import ParticipationForm, {
  IParticipationFormProps,
} from "./ParticipationForm/ParticipationForm";
import FlexBox from "design-system/src/Components/FlexBox/FlexBox";
import ParticipationStep1 from "./ParticipationStep1/ParticipationStep1";
import TypographyContext from "design-system/src/Foundation/Typography/Typography.context";
import ButtonContext from "design-system/src/Foundation/Button/Button.context";
import ParticipationConfirmation from "./ParticipationConfirmation/ParticipationConfirmation";
import useBackboneContext from "@src/backbone/provider/useBackboneContext";

interface IGameParticipationModalProps extends Omit<IModalProps, "show"> {
  data?: IPacmanGameData;
}

type IStep =
  | {
      type: 1;
    }
  | {
      type: 2;
      data: IParticipationFormProps["data"];
      previous: IStep;
    }
  | {
      // Success
      type: 3;
    };

const GameParticipationModal = (props: IGameParticipationModalProps) => {
  const { data, ...modalProps } = props;
  const show = !!data;
  const { isMobile } = useBreakpoint();
  const { app } = useBackboneContext();
  const { t } = useTranslation(["Pacman"]);
  const [currentStep, setCurrentStep] = useState<IStep>({ type: 1 });

  const handleOpenForm = useCallback(() => {
    if (data) {
      setCurrentStep((c) => ({
        type: 2,
        previous: c,
        data,
      }));
    } else {
      console.error("No data to open form");
    }
  }, [data]);

  const handleBack = useCallback(() => {
    setCurrentStep((c) => {
      if (c.type === 2 && c.previous) {
        return c.previous;
      }
      return c;
    });
  }, []);

  const handleClose = useCallback(() => {
    modalProps.onCancel?.();
    setCurrentStep({
      type: 1,
    });
  }, [modalProps]);

  const handleFormSuccess = useCallback(() => {
    setCurrentStep({
      type: 3,
    });
  }, []);

  const handleFinish = useCallback(() => {
    setCurrentStep({
      type: 1,
    });
    app.go("listing");
    modalProps.onCancel?.();
  }, [app, modalProps]);

  return (
    <TypographyContext.Provider value={{}}>
      <ButtonContext.Provider
        value={{
          textColor: undefined,
          backgroundColor: undefined,
        }}
      >
        <Modal
          type={"transparent-modal"}
          closable={false}
          show={show}
          zIndex={10}
          style={
            isMobile
              ? {
                  alignItems: "flex-end",
                  padding: "36px 16px",
                }
              : undefined
          }
          overlayStyle={{ opacity: 0.6, backgroundColor: "#000" }}
          bodyStyle={{
            width: "100%",
            maxWidth: 559,
            maxHeight: "80vh",
            marginTop: 64,
            overflow: "auto",
            padding: 0,
          }}
          content={
            <div>
              <div style={{ position: "sticky", top: 0 }}>
                {currentStep.type === 2 && !!currentStep.previous && (
                  <div
                    style={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      cursor: "pointer",
                      zIndex: 10,
                      padding: 15,
                    }}
                    onClick={handleBack}
                  >
                    <Icon
                      icon={"caret_down"}
                      style={{ transform: "rotate(90deg)" }}
                    />
                  </div>
                )}
                <div
                  style={{
                    position: "absolute",
                    right: 0,
                    top: 0,
                    cursor: "pointer",
                    zIndex: 10,
                    padding: 15,
                  }}
                  onClick={handleClose}
                >
                  <Icon
                    icon={"cross_close"}
                    style={{ transform: "rotate(90deg)" }}
                  />
                </div>
              </div>
              <_AccountOrderHelpModalBody>
                <FlexBox direction="column" align="center">
                  <Typography
                    variant={isMobile ? "h6" : "h5"}
                    mb={24}
                    align="center"
                  >
                    {t("Sushi Shop x Pac-man", "Pacman")}
                  </Typography>
                  <FlexBox direction="column" style={{ width: "100%" }}>
                    {currentStep.type === 1 && (
                      <ParticipationStep1 onOpenForm={handleOpenForm} />
                    )}
                    {currentStep.type === 2 && currentStep.data && (
                      <ParticipationForm
                        data={currentStep.data}
                        onSuccess={handleFormSuccess}
                      />
                    )}
                    {currentStep.type === 3 && (
                      <ParticipationConfirmation onOk={handleFinish} />
                    )}
                  </FlexBox>
                </FlexBox>
                {show && (
                  <style
                    id="body-override"
                    dangerouslySetInnerHTML={{
                      __html: `
                        body {
                        overflow: hidden;             
                        }`,
                    }}
                  />
                )}
              </_AccountOrderHelpModalBody>
            </div>
          }
          {...modalProps}
        />
      </ButtonContext.Provider>
    </TypographyContext.Provider>
  );
};

export default GameParticipationModal;
